import { t } from "i18next";
import InitSurveyLogic, { SurveyAnswer } from "./Survey.logic";
import {
  SURVEY_GENERAL_ITEMS,
  SURVEY_TRAINERS,
} from "../../config/SurveyItems";
import styles from "./Survey.module.css";

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface RatingRowProps {
  itemKey: string;
  label: string;
  answer: SurveyAnswer;
  onScore: (itemKey: string, score: number) => void;
  onComment: (itemKey: string, comment: string) => void;
}

const RatingRow: React.FC<RatingRowProps> = ({
  itemKey,
  label,
  answer,
  onScore,
  onComment,
}) => (
  <div className={styles.item}>
    <div className={styles.itemLabel}>{`${t(label)}`}</div>
    <div className={styles.scores} role="group" aria-label={`${t(label)}`}>
      {SCORES.map((score) => (
        <button
          key={score}
          type="button"
          aria-pressed={answer?.score === score}
          className={`${styles.score} ${
            answer?.score === score ? styles.scoreActive : ""
          }`}
          onClick={() => onScore(itemKey, score)}
        >
          {score}
        </button>
      ))}
    </div>
    <input
      type="text"
      className={styles.comment}
      placeholder={`${t("Komentar ocene")}`}
      value={answer?.comment ?? ""}
      onChange={(e) => onComment(itemKey, e.target.value)}
    />
  </div>
);

const SurveyComponent: React.FC = () => {
  const {
    status,
    showModal,
    submitting,
    anonymous,
    setAnonymous,
    suggestions,
    setSuggestions,
    answers,
    setScore,
    setComment,
    openModal,
    closeModal,
    onSubmit,
  } = InitSurveyLogic();

  if (status !== "todo") return null;

  return (
    <>
      {/* Baner - ostaje na vrhu dok se upitnik ne popuni */}
      {!showModal && (
        <div className={styles.banner}>
          <span className={styles.bannerText}>{`${t("Upitnik")}`}</span>
          <button
            type="button"
            className={`btn-primary ${styles.bannerButton}`}
            onClick={openModal}
          >
            {`${t("Popuni")}`}
          </button>
        </div>
      )}

      {showModal && (
        <div className={styles.overlay} onClick={closeModal}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="survey-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.header}>
              <h2 id="survey-title" className={styles.title}>
                {`${t("Upitnik o kvalitetu usluge")}`}
              </h2>
              <button
                type="button"
                className={styles.close}
                aria-label={`${t("Zatvori")}`}
                onClick={closeModal}
              >
                ×
              </button>
            </div>

            <div className={styles.body}>
              <p className={styles.intro}>
                {`${t(
                  "Molimo Vas da iskreno odgovorite na pitanja u nastavku kako bismo dobili što kvalitetniju informaciju o Vašim utiscima i mogućnostima za unapređenje.",
                )}`}
              </p>
              <p className={styles.intro}>
                {`${t("Hvala unapred na pažnji i izdvojenom vremenu.")}`}
              </p>

              {SURVEY_GENERAL_ITEMS.map((item) => (
                <RatingRow
                  key={item.itemKey}
                  itemKey={item.itemKey}
                  label={item.label}
                  answer={answers[item.itemKey]}
                  onScore={setScore}
                  onComment={setComment}
                />
              ))}

              <div className={styles.sectionHeading}>
                {`${t("Treneri")}`}
                <span className={styles.asterisk}>*</span>
              </div>
              <p className={styles.note}>
                <span className={styles.asterisk}>*</span>
                {` ${t(
                  "Ocenite iskreno rad, ljubaznost i posvećenost trenera s kojima ste imali priliku da trenirate.",
                )}`}
              </p>

              {SURVEY_TRAINERS.map((item) => (
                <RatingRow
                  key={item.itemKey}
                  itemKey={item.itemKey}
                  label={item.label}
                  answer={answers[item.itemKey]}
                  onScore={setScore}
                  onComment={setComment}
                />
              ))}

              <div className={styles.item}>
                <div className={styles.itemLabel}>
                  {`${t("Predlozi za unapređenje")}`}
                </div>
                <input
                  type="text"
                  className={styles.comment}
                  placeholder={`${t("Unesite Vaš predlog")}`}
                  value={suggestions}
                  onChange={(e) => setSuggestions(e.target.value)}
                />
              </div>

              <div className={styles.anonymous}>
                <label className={styles.anonymousRow}>
                  <input
                    type="checkbox"
                    checked={anonymous}
                    onChange={(e) => setAnonymous(e.target.checked)}
                  />
                  <span className={styles.anonymousLabel}>
                    {`${t("Anonimno")}`}
                  </span>
                </label>
              </div>

              <p className={styles.signature}>
                {`${t("Hvala")}`}
                <br />
                {`${t("RESET tim")}`}
              </p>
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={`btn-secondary ${styles.footerButton}`}
                onClick={closeModal}
              >
                {`${t("Popuni kasnije")}`}
              </button>
              <button
                type="button"
                className={`btn-primary ${styles.footerButton}`}
                disabled={submitting}
                onClick={onSubmit}
              >
                {submitting ? `${t("Slanje...")}` : `${t("Pošalji")}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SurveyComponent;
